/**
 * AvatarPackResult — standalone viewer for a purchased avatar pack at
 * /me/avatar-pack/:id. Lets "My Rites" reopen a completed pack instead of the
 * buy page. Reuses the useAvatarPack polling hook; renders a simple frame grid.
 */
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { useAvatarPack } from '../../hooks/useAvatarPack';
import { AiGeneratedLabel } from '../../components/AiGeneratedLabel';

export function AvatarPackResult(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation('common');
  const { snapshot, error } = useAvatarPack(id);

  // Guard a malformed deep link (e.g. /me/avatar-pack/ with no id) — otherwise
  // useAvatarPack('') no-ops and the page would spin forever.
  if (!id) {
    return (
      <MeSubpageShell title={t('results.avatarTitle')} data-testid="avatar-pack-result-page">
        <p role="alert">{t('results.invalidLink')}</p>
      </MeSubpageShell>
    );
  }

  const status = snapshot?.status;
  const ready = status === 'COMPLETED';
  const failed = status === 'FAILED';
  const pending = !!snapshot && !ready && !failed;

  return (
    <MeSubpageShell title={t('results.avatarTitle')} data-testid="avatar-pack-result-page">
      {error && <p role="alert">{t('errors.load')}</p>}
      {!error && !snapshot && <p>{t('states.loading')}</p>}
      {!error && failed && <p role="alert">{t('results.couldNotGenerate')}</p>}
      {!error && pending && <p>{t('results.stillPreparing')}</p>}
      {!error && ready && (
        <>
          <AiGeneratedLabel />
          <div
            data-testid="avatar-pack-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 10,
            }}
          >
            {snapshot!.frames.map((frame) => (
              <a key={frame.id} href={frame.imageUrl} target="_blank" rel="noreferrer">
                <img
                  src={frame.thumbnailUrl || frame.imageUrl}
                  alt={t('results.frameAlt')}
                  loading="lazy"
                  style={{ width: '100%', borderRadius: 12, display: 'block' }}
                />
              </a>
            ))}
          </div>
        </>
      )}
    </MeSubpageShell>
  );
}
